import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { ClassRoomsApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";
import { useToast } from "@/components/feedback";

export const useClassRoomDelete = () => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate, isPending } = useMutation({
    mutationFn: async (classRoomId: number) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      const service = createAuthenticatedApi(ClassRoomsApi, accessToken);
      return service.apiClassRoomsIdDelete({ id: classRoomId });
    },
    onSuccess: async (_data, classRoomId) => {
      queryClient.removeQueries({ queryKey: [QueryKeys.ClassRooms, classRoomId] });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.ClassRooms] });
      toast({
        title: "Classroom deleted",
        description: "Classroom has been deleted successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: unknown) => {
      const errorMessage = await getApiErrorMessage(
        error,
        "This classroom is still in use and cannot be deleted. Remove or reassign linked classes first.",
      );
      toast({
        title: "Cannot delete classroom",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending };
};
